import Select from '@/components/form/select';
import Text from '@/components/text';
import { FormHandle } from '@/hooks/use-form-handle.hook';
import { PolicyModel } from '@/lib/models/policy-model/policy.model';
import { Box, Stack, Theme } from '@mui/material';
import { cloneDeep } from 'lodash';
import { FC, useCallback, useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { getFormattedUiOptions } from '../select/helpers';
import {
  ContentControlConfig,
  ContentControlEntity,
  ContentControlSettings,
  ContentControlType,
  getContentControlActionOptions,
} from '@/lib/models/policy-model/content-controls';
import { ControlActionValue } from '@/lib/models/policy-model/controls';

interface Props {
  policy: PolicyModel;
  formHandle: FormHandle<PolicyModel>;
  readonly: boolean;
  controlType: ContentControlType;
  defaultPolicy?: PolicyModel;
  withNotSet?: boolean;
}

const useStyles = createUseStyles((theme: Theme) => ({
  contentControlItem: {
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
      marginBottom: 8,
      paddingBottom: 8,
    },
  },
}));

const ContentControlItem: FC<Props> = ({
  controlType,
  formHandle,
  readonly,
  defaultPolicy,
  withNotSet = true,
}) => {
  const styles = useStyles();

  const { values, setFieldValue } = formHandle;

  const handleChange = useCallback(
    (controlType: ContentControlType, entityIndex: number, action: string | undefined) => {
      if (!values.contentControls) {
        values.contentControls = new ContentControlConfig({});
      }

      const updated = values.contentControls.entities
        ? cloneDeep(values.contentControls.entities)
        : [];

      if (entityIndex > -1) {
        if (action) {
          updated[entityIndex].action = action as ControlActionValue;
        } else {
          updated.splice(entityIndex, 1);
        }
      } else if (action) {
        updated.push({ type: controlType, action: action as ControlActionValue });
      }

      setFieldValue('contentControls.entities', updated);
    },
    [values, setFieldValue]
  );

  const actionOptionsMap = useMemo(() => getContentControlActionOptions(), []);

  const [contentControl, entityIndex, entity, entityActionOptions] = useMemo(() => {
    const contentControl = ContentControlSettings.find(({ type }) => type === controlType)!;

    let entity: ContentControlEntity | null = null;
    let entityIndex = -1;

    if (values.contentControls?.entities) {
      entityIndex = values.contentControls?.entities.findIndex(({ type }) => type === controlType);
      entity = entityIndex > -1 ? values.contentControls?.entities[entityIndex] : null;
    }

    const defaultPolicyEntity = defaultPolicy?.contentControls?.entities.find(
      ({ type }) => type === controlType
    );
    const defaultPolicyAction = defaultPolicyEntity?.action;

    const baseOptions = (actionOptionsMap.get(controlType) || []).map((option) => {
      if (option.meta?.header) {
        return option;
      }
      const isDefaultPolicy = option.value === defaultPolicyAction;
      return {
        ...option,
        meta: { ...option.meta, orgPolicyLevel: isDefaultPolicy },
      };
    });

    const entityActionOptions = getFormattedUiOptions(baseOptions);

    return [contentControl, entityIndex, entity, entityActionOptions];
  }, [values.contentControls?.entities, controlType, actionOptionsMap, defaultPolicy]);

  const { label, description } = contentControl;

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      className={styles.contentControlItem}
    >
      <Box pr={4}>
        <Text>{label}</Text>
        <Box mt={0.5}>
          <Text size="small" color="grey">
            {description}
          </Text>
        </Box>
      </Box>
      <Box minWidth={250}>
        <Select
          name={`contentControls.${controlType}`}
          options={entityActionOptions}
          readonly={readonly}
          value={entity?.action}
          onChange={(value: string | undefined) => handleChange(controlType, entityIndex, value)}
          size="small"
          withNotSet={withNotSet}
        />
      </Box>
    </Stack>
  );
};

export default ContentControlItem;
